/**
 * Weekly-off policy editor — HR picks which weekdays are paid weekly offs for this
 * institute / branch (plus an optional Saturday rule). Drives the whole day tally.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SAT_RULES = [
  { value: 'none', label: 'No extra Saturday off' },
  { value: 'all', label: 'Every Saturday off' },
  { value: '2_4', label: '2nd & 4th Saturday off' },
  { value: '1_3', label: '1st & 3rd Saturday off' },
];

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

export function WeeklyOffPolicyCard({ entityId, policy, disabled, onSaved }) {
  const [days, setDays] = useState(policy?.weekly_off_days || [6]);
  const [rule, setRule] = useState(policy?.saturday_rule || 'none');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDays(policy?.weekly_off_days || [6]);
    setRule(policy?.saturday_rule || 'none');
  }, [policy]);

  const toggle = (idx) => {
    setDays((prev) => (prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx].sort()));
  };

  const save = async () => {
    if (!entityId) return;
    if (days.length >= 7) {
      toast.error('At least one weekday must remain a working day.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/attendance/weekly-off-policy/${entityId}`, {
        method: 'PUT',
        headers: headers(),
        credentials: 'include',
        body: JSON.stringify({ weekly_off_days: days, saturday_rule: rule }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed to save weekly-off policy');
      toast.success(d.message);
      onSaved?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="weekly-off-policy-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <CalendarCheck className="w-4 h-4 text-blue-600" />
          Weekly Off Policy
          <Badge variant="outline" className="ml-1 text-[10px] font-normal" data-testid="weekly-off-current">
            {policy?.text || 'Sunday'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {DAYS.map((d, idx) => {
            const on = days.includes(idx);
            return (
              <button
                key={d}
                type="button"
                disabled={disabled || saving}
                onClick={() => toggle(idx)}
                data-testid={`weekoff-day-${idx}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  on
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                {d}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Saturday rule</p>
            <Select value={rule} onValueChange={setRule} disabled={disabled || saving}>
              <SelectTrigger className="h-8 w-56" data-testid="weekoff-sat-rule">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SAT_RULES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={save} disabled={disabled || saving} data-testid="weekoff-save-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            Save weekly off
          </Button>
        </div>
        <p className="text-[11px] text-slate-500">
          Weekly offs are PAID and need no attendance. Changing this instantly re-computes the day
          tally for every staff member of this institute / branch.
        </p>
      </CardContent>
    </Card>
  );
}

export default WeeklyOffPolicyCard;
